// TradingViewSignalMonitor.jsx - Updated with historical data loading

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { sameOriginWs } from '../../utils/wsUrl';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Card,
  CardContent,
  alpha,
  Container,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  SignalCellularAlt as SignalIcon,
} from '@mui/icons-material';

const DS = {
  colors: {
    background: { primary: '#0f1419', secondary: '#1a1f2e', tertiary: '#13161b' },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
      tertiary: 'rgba(255, 255, 255, 0.5)',
      muted: 'rgba(255, 255, 255, 0.3)',
    },
    status: {
      success: '#26a69a',
      error: '#f23645',
      warning: '#ffa500',
      info: '#1e88e5',
    },
    accent: { primary: '#1e88e5', secondary: '#764ba2', tertiary: '#26a69a' },
  },
};

const TradingViewSignalMonitor = ({ 
  wsUrl = sameOriginWs('/ws/tradingview-signals/'),
  apiBaseUrl = window.location.origin
}) => {
  const [signals, setSignals] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const [filterType, setFilterType] = useState('all');
  const [filterTimeframe, setFilterTimeframe] = useState('all');
  const [filterAsset, setFilterAsset] = useState('all');
  const [filterTradingHour, setFilterTradingHour] = useState('all');

  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const pingInterval = useRef(null);
  const isManualClose = useRef(false);
  const hasLoadedHistorical = useRef(false);

  // Load historical signals from database
  const loadHistoricalSignals = useCallback(async () => {
    if (hasLoadedHistorical.current) {
      console.log('[API] Historical data already loaded');
      return;
    }

    try {
      setLoading(true);
      console.log('[API] Loading historical signals...');
      
      const response = await fetch(`${apiBaseUrl}/strategies/api/signals/historical/?limit=1000&hours=24`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      if (data.signals && Array.isArray(data.signals)) {
        const historicalSignals = data.signals.map(signal => ({
          id: `hist-${signal.timestamp}-${Math.random()}`,
          ...signal,
          receivedAt: new Date(signal.timestamp),
        }));

        setSignals(historicalSignals);
        hasLoadedHistorical.current = true;
        console.log(`[API] ✅ Loaded ${historicalSignals.length} historical signals`);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('[API] Failed to load historical signals:', err);
      setError(`Failed to load historical data: ${err.message}`);
      setLoading(false);
    }
  }, [apiBaseUrl]);

  const connectWebSocket = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      console.log('[WS] Already connected');
      return;
    }

    try {
      console.log(`[WS] Connecting to ${wsUrl}...`);
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log('[WS] ✅ Connected');
        setConnected(true);
        setError(null);
        setReconnectAttempts(0);

        // Start heartbeat ping every 30 seconds
        pingInterval.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            try {
              ws.current.send(JSON.stringify({ type: 'ping' }));
              console.log('[WS] 💓 Ping sent');
            } catch (e) {
              console.error('[WS] Ping failed:', e);
            }
          }
        }, 30000);
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[WS] Message received:', data);

          // Ignore connection/ping messages
          if (data.type === 'connection' || data.type === 'pong') {
            return;
          }

          // Validate signal has required fields
          if (data.signal && data.asset && data.timeframe && data.price) {
            addSignal(data);
          } else {
            console.warn('[WS] Incomplete signal data:', data);
          }
        } catch (e) {
          console.error('[WS] Failed to parse message:', e);
        }
      };

      ws.current.onerror = (error) => {
        console.error('[WS] ❌ Error:', error);
        setError('WebSocket connection error');
      };

      ws.current.onclose = (event) => {
        console.log(`[WS] ❌ Disconnected (code: ${event.code})`);
        setConnected(false);

        // Clear ping interval
        if (pingInterval.current) {
          clearInterval(pingInterval.current);
          pingInterval.current = null;
        }

        // Don't reconnect if manually closed
        if (isManualClose.current) {
          console.log('[WS] Manual close - not reconnecting');
          return;
        }

        // Exponential backoff reconnection
        const backoffDelay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
        console.log(`[WS] Reconnecting in ${backoffDelay}ms (attempt ${reconnectAttempts + 1})`);
        
        setReconnectAttempts(prev => prev + 1);
        reconnectTimeout.current = setTimeout(() => {
          connectWebSocket();
        }, backoffDelay);
      };
    } catch (e) {
      console.error('[WS] Connection setup failed:', e);
      setError('Failed to establish connection');
    }
  }, [wsUrl, reconnectAttempts]);

  // Initialize: Load historical data first, then connect WebSocket
  useEffect(() => {
    const initialize = async () => {
      await loadHistoricalSignals();
      
      // Wait a bit before connecting WebSocket to ensure data is loaded
      setTimeout(() => {
        isManualClose.current = false;
        connectWebSocket();
      }, 500);
    };

    initialize();

    return () => {
      isManualClose.current = true;
      
      if (pingInterval.current) {
        clearInterval(pingInterval.current);
      }
      
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [loadHistoricalSignals, connectWebSocket]);

  const addSignal = (signal) => {
    const newSignal = {
      id: `live-${Date.now()}-${Math.random()}`,
      ...signal,
      receivedAt: new Date(),
    };

    console.log('[SIGNAL] Added:', newSignal);
    
    // Add to top of list, keep max 1000 signals
    setSignals((prev) => [newSignal, ...prev].slice(0, 1000));
    setPage(0);
  };

  const handleReset = async () => {
    console.log('[RESET] Clearing and reloading...');
    
    // Close WebSocket
    isManualClose.current = true;
    if (ws.current) {
      ws.current.close();
    }
    
    // Reset state
    setSignals([]);
    setPage(0);
    setReconnectAttempts(0);
    hasLoadedHistorical.current = false;
    
    // Reload historical data and reconnect
    await loadHistoricalSignals();
    
    setTimeout(() => {
      isManualClose.current = false;
      connectWebSocket();
    }, 500);
  };

  const uniqueAssets = [...new Set(signals.map((s) => s.asset).filter(Boolean))];
  const usedTimeframes = [...new Set(signals.map((s) => s.timeframe).filter(Boolean))];
  const usedTradingHours = [...new Set(signals.map((s) => s.trading_hour).filter(Boolean))];

  const filteredSignals = signals.filter((s) => {
    if (filterType !== 'all') {
      if (filterType === 'historical' && !s.is_historical) return false;
      if (filterType === 'realtime' && s.is_historical) return false;
    }
    if (filterTimeframe !== 'all' && s.timeframe !== filterTimeframe) return false;
    if (filterAsset !== 'all' && s.asset !== filterAsset) return false;
    if (filterTradingHour !== 'all' && s.trading_hour !== filterTradingHour) return false;
    return true;
  });

  const paginatedSignals = filteredSignals.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const realTimeSignals = signals.filter((s) => !s.is_historical);
  const historicalSignals = signals.filter((s) => s.is_historical);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ fontSize: '20px', fontWeight: 600, color: DS.colors.text.primary }}>
              📊 TradingView Signal Monitor
            </Typography>
            <Typography sx={{ fontSize: '12px', color: DS.colors.text.tertiary, mt: 0.5 }}>
              Real-time signal tracking from TradingView webhooks
            </Typography>
          </Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              color: DS.colors.accent.primary,
              border: `1px solid ${alpha(DS.colors.accent.primary, 0.3)}`,
              '&:hover': {
                bgcolor: alpha(DS.colors.accent.primary, 0.08),
              },
              '&:disabled': {
                opacity: 0.5,
              },
            }}
          >
            Reset
          </Button>
        </Box>

        {/* Connection Status Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ bgcolor: alpha(DS.colors.status.error, 0.1), border: `1px solid ${alpha(DS.colors.status.error, 0.3)}` }}
          >
            {error} - Reconnect attempt {reconnectAttempts}
          </Alert>
        )}

        {loading && (
          <Alert 
            severity="info" 
            icon={<CircularProgress size={20} />}
            sx={{ bgcolor: alpha(DS.colors.status.info, 0.1), border: `1px solid ${alpha(DS.colors.status.info, 0.3)}` }}
          >
            Loading historical signals from database...
          </Alert>
        )}

        {!connected && !loading && !error && (
          <Alert 
            severity="warning" 
            sx={{ bgcolor: alpha(DS.colors.status.warning, 0.1), border: `1px solid ${alpha(DS.colors.status.warning, 0.3)}` }}
          >
            Connecting to live signal feed... (Attempt {reconnectAttempts + 1})
          </Alert>
        )}

        {/* Status Cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                bgcolor: alpha(DS.colors.status.success, 0.08),
                border: `1px solid ${alpha(DS.colors.status.success, 0.2)}`,
                borderRadius: 1.5,
              }}
            >
              <CardContent sx={{ pb: 2 }}>
                <Typography sx={{ color: DS.colors.text.tertiary, fontSize: '11px', mb: 1 }}>
                  Real-Time Signals
                </Typography>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: DS.colors.status.success }}>
                  {realTimeSignals.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                bgcolor: alpha(DS.colors.status.info, 0.08),
                border: `1px solid ${alpha(DS.colors.status.info, 0.2)}`,
                borderRadius: 1.5,
              }}
            >
              <CardContent sx={{ pb: 2 }}>
                <Typography sx={{ color: DS.colors.text.tertiary, fontSize: '11px', mb: 1 }}>
                  Historical Signals
                </Typography>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: DS.colors.status.info }}>
                  {historicalSignals.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                bgcolor: alpha(DS.colors.accent.primary, 0.08),
                border: `1px solid ${alpha(DS.colors.accent.primary, 0.2)}`,
                borderRadius: 1.5,
              }}
            >
              <CardContent sx={{ pb: 2 }}>
                <Typography sx={{ color: DS.colors.text.tertiary, fontSize: '11px', mb: 1 }}>
                  Total Signals
                </Typography>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: DS.colors.accent.primary }}>
                  {signals.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                bgcolor: connected
                  ? alpha(DS.colors.status.success, 0.08)
                  : alpha(DS.colors.status.error, 0.08),
                border: connected
                  ? `1px solid ${alpha(DS.colors.status.success, 0.2)}`
                  : `1px solid ${alpha(DS.colors.status.error, 0.2)}`,
                borderRadius: 1.5,
              }}
            >
              <CardContent sx={{ pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <SignalIcon sx={{ fontSize: 16, color: DS.colors.text.tertiary }} />
                  <Typography sx={{ color: DS.colors.text.tertiary, fontSize: '11px' }}>
                    Connection
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: connected ? DS.colors.status.success : DS.colors.status.error,
                  }}
                >
                  {loading ? '⏳ Loading...' : connected ? '🟢 Live' : '🔴 Offline'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper
          sx={{
            bgcolor: 'rgba(26, 31, 46, 0.6)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 1.5,
            p: 2,
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: DS.colors.text.secondary, mb: 2 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: DS.colors.text.secondary, fontSize: '12px' }}>Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setPage(0);
                  }}
                  label="Type"
                  sx={{
                    color: DS.colors.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  <MenuItem value="all">All Signals</MenuItem>
                  <MenuItem value="realtime">Real-Time Only</MenuItem>
                  <MenuItem value="historical">Historical Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: DS.colors.text.secondary, fontSize: '12px' }}>Asset</InputLabel>
                <Select
                  value={filterAsset}
                  onChange={(e) => {
                    setFilterAsset(e.target.value);
                    setPage(0);
                  }}
                  label="Asset"
                  sx={{
                    color: DS.colors.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  <MenuItem value="all">All Assets</MenuItem>
                  {uniqueAssets.map((asset) => (
                    <MenuItem key={asset} value={asset}>
                      {asset}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: DS.colors.text.secondary, fontSize: '12px' }}>
                  Timeframe
                </InputLabel>
                <Select
                  value={filterTimeframe}
                  onChange={(e) => {
                    setFilterTimeframe(e.target.value);
                    setPage(0);
                  }}
                  label="Timeframe"
                  sx={{
                    color: DS.colors.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  <MenuItem value="all">All Timeframes</MenuItem>
                  {usedTimeframes.map((tf) => (
                    <MenuItem key={tf} value={tf}>
                      {tf}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: DS.colors.text.secondary, fontSize: '12px' }}>
                  Trading Hour
                </InputLabel>
                <Select
                  value={filterTradingHour}
                  onChange={(e) => {
                    setFilterTradingHour(e.target.value);
                    setPage(0);
                  }}
                  label="Trading Hour"
                  sx={{
                    color: DS.colors.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  <MenuItem value="all">All Trading Hours</MenuItem>
                  {usedTradingHours.map((th) => (
                    <MenuItem key={th} value={th}>
                      {th}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Signals Table */}
        <TableContainer
          component={Paper}
          sx={{
            bgcolor: 'rgba(26, 31, 46, 0.6)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 1.5,
            backdropFilter: 'blur(8px)',
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(26, 31, 46, 0.8)' }}>
                <TableCell sx={{ color: DS.colors.text.secondary, fontSize: '11px', fontWeight: 600, py: 1.5 }}>
                  Type
                </TableCell>
                <TableCell sx={{ color: DS.colors.text.secondary, fontSize: '11px', fontWeight: 600 }}>
                  Signal
                </TableCell>
                <TableCell sx={{ color: DS.colors.text.secondary, fontSize: '11px', fontWeight: 600 }}>
                  Asset
                </TableCell>
                <TableCell sx={{ color: DS.colors.text.secondary, fontSize: '11px', fontWeight: 600 }}>
                  Timeframe
                </TableCell>
                <TableCell sx={{ color: DS.colors.text.secondary, fontSize: '11px', fontWeight: 600 }}>
                  Trading Hour
                </TableCell>
                <TableCell sx={{ color: DS.colors.text.secondary, fontSize: '11px', fontWeight: 600 }}>
                  Price
                </TableCell>
                <TableCell sx={{ color: DS.colors.text.secondary, fontSize: '11px', fontWeight: 600 }}>
                  Time
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: DS.colors.accent.primary }} size={30} />
                    <Typography sx={{ mt: 1, color: DS.colors.text.tertiary, fontSize: '12px' }}>
                      Loading historical signals...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredSignals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    {connected ? (
                      <>
                        <SignalIcon sx={{ color: DS.colors.accent.primary, fontSize: 40 }} />
                        <Typography sx={{ mt: 1, color: DS.colors.text.tertiary, fontSize: '12px' }}>
                          No signals found. Listening for new signals...
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CircularProgress sx={{ color: DS.colors.accent.primary }} size={30} />
                        <Typography sx={{ mt: 1, color: DS.colors.text.tertiary, fontSize: '12px' }}>
                          Connecting to signal server...
                        </Typography>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSignals.map((signal) => (
                  <TableRow
                    key={signal.id}
                    sx={{
                      bgcolor: signal.is_historical ? 'rgba(26, 31, 46, 0.3)' : 'rgba(38, 166, 154, 0.05)',
                      '&:hover': { bgcolor: 'rgba(26, 31, 46, 0.6)' },
                      borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <TableCell>
                      <Chip
                        icon={signal.is_historical ? <HistoryIcon /> : <TrendingUpIcon />}
                        label={signal.is_historical ? 'Historical' : 'Live'}
                        size="small"
                        sx={{
                          bgcolor: signal.is_historical
                            ? alpha(DS.colors.status.warning, 0.2)
                            : alpha(DS.colors.status.success, 0.2),
                          color: signal.is_historical ? DS.colors.status.warning : DS.colors.status.success,
                          fontSize: '10px',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: DS.colors.text.primary }}>
                        {signal.signal || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '12px', color: DS.colors.text.secondary, fontWeight: 600 }}>
                        {signal.asset || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '11px', color: DS.colors.text.tertiary }}>
                        {signal.timeframe || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '11px', color: DS.colors.accent.secondary }}>
                        {signal.trading_hour || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '11px', color: DS.colors.accent.primary, fontWeight: 600 }}>
                        {signal.price ? parseFloat(signal.price).toFixed(2) : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '10px', color: DS.colors.text.muted }}>
                        {signal.timestamp ? new Date(signal.timestamp).toLocaleTimeString() : 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredSignals.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              bgcolor: 'rgba(26, 31, 46, 0.8)',
              color: DS.colors.text.secondary,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '11px',
              },
            }}
          />
        </TableContainer>
      </Stack>
    </Container>
  );
};

export default TradingViewSignalMonitor;
